import { Injectable, inject } from '@angular/core';
import { Observable, map, tap } from 'rxjs';
import { ApiService } from './api.service';
import { AuthService } from './auth.service';

export interface LoginRequest {
  username: string;
  password: string;
}

export interface RegisterRequest {
  username: string;
  email: string;
  password: string;
  fullName: string;
  tenantId: string;
  businessUnitId?: string;
}

export interface RedeemInvitationRequest {
  invitationCode: string;
  email: string;
  username: string;
  password: string;
  fullName: string;
  phone?: string;
}

export interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  expiration: string;
}

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private apiService = inject(ApiService);
  private authService = inject(AuthService);

  login(credentials: LoginRequest): Observable<any> {
    return this.apiService.post<LoginResponse>('auth/login', credentials).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.authService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
        }
      })
    );
  }

  register(request: RegisterRequest): Observable<any> {
    return this.apiService.post<LoginResponse>('auth/register', request).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.authService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
        }
      })
    );
  }

  acceptInvitation(request: RedeemInvitationRequest): Observable<any> {
    return this.apiService.post<LoginResponse>('auth/accept-invitation', request).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.authService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
        }
      })
    );
  }

  googleOAuthLogin(code: string, redirectUri: string): Observable<any> {
    return this.apiService.post<LoginResponse>('auth/oauth/google', { code, redirectUri }).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.authService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
        }
      })
    );
  }

  switchTenant(tenantId: string): Observable<void> {
    return this.apiService.post<{ accessToken: string }>('auth/switch-tenant', { tenantId }).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.authService.setToken(response.data.accessToken);
        }
      }),
      map(() => void 0)
    );
  }

  switchBusinessUnit(businessUnitId: string): Observable<void> {
    return this.apiService.post<{ accessToken: string }>('auth/switch-business-unit', { businessUnitId }).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.authService.setToken(response.data.accessToken);
        }
      }),
      map(() => void 0)
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<any> {
    return this.apiService.post<LoginResponse>('auth/change-password', {
      currentPassword,
      newPassword
    }).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.authService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
        }
      })
    );
  }

  logout(): void {
    const refreshToken = this.authService.getRefreshToken();
    if (refreshToken) {
      this.apiService.post('auth/logout', { refreshToken }).subscribe();
    }
    this.authService.logout();
  }

  refreshToken(): Observable<any> {
    const refreshToken = this.authService.getRefreshToken();
    const accessToken = this.authService.getAccessToken();
    
    if (!refreshToken || !accessToken) {
      throw new Error('No tokens available');
    }

    return this.apiService.post<LoginResponse>('auth/refresh', {
      accessToken,
      refreshToken
    }).pipe(
      tap(response => {
        if (response.success && response.data) {
          this.authService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );
        }
      })
    );
  }
}

